import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  RadioButtonUnchecked,
  ArrowBack,
} from '@mui/icons-material';
import {useSubscription} from 'context/SubscriptionContext';
import {useSubscriptionPricing} from 'context/SubscriptionPricingContext';
import {useSubscriptionConfig} from 'context/SubscriptionConfigContext';
import {useDuration} from 'context/DurationContext';
import {useCountry} from 'context/CountryContext';
import {ENTITY_CONFIG} from 'config/entityConfig';
import {
  SubscriptionTypes,
  SubscriptionTypeLabels,
} from 'constants/subscriptionTypes';
import {
  isDiscountInputAllowed,
  shouldShowSection,
} from 'utils/subscriptionRulesUtils';

const Row = ({label, value}) => (
  <Box display="flex" alignItems="flex-start" gap={1} py={0.75}>
    {value ? (
      <CheckCircle sx={{fontSize: 18, color: '#16a34a', mt: '2px'}} />
    ) : (
      <RadioButtonUnchecked sx={{fontSize: 18, color: '#cbd5e1', mt: '2px'}} />
    )}
    <Box minWidth={0}>
      <Typography variant="caption" color="textSecondary" display="block">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{fontWeight: 600, color: value ? '#1e293b' : '#94a3b8'}}>
        {value || 'Not selected'}
      </Typography>
    </Box>
  </Box>
);

// Single live summary + the only submit button on the page.
const SubscriptionSummaryPanel = ({
  isEditMode,
  isLoading,
  disabled,
  onSubmit,
  onBack,
}) => {
  const {subscriptionType, moduleType, duration, country} = useSubscription();
  const {pricingStrategy, getGlobalPricing, getAllOverrides} =
    useSubscriptionPricing();
  const {config} = useSubscriptionConfig();
  const {getDurationById} = useDuration();
  const {getCountryById} = useCountry();

  const selectedDuration = duration ? getDurationById(duration) : null;
  const selectedCountry = country ? getCountryById(country) : null;

  const strategy = pricingStrategy?.strategy;
  const discountAllowed = isDiscountInputAllowed(subscriptionType);
  const showProRata = shouldShowSection(subscriptionType, 'showProRataRefund');

  const completed = [
    subscriptionType,
    moduleType,
    duration,
    country,
    strategy,
  ].filter(Boolean).length;

  let pricingText = null;
  if (strategy === 'global') {
    const discount = getGlobalPricing(subscriptionType)?.discountPercentage || 0;
    pricingText = discountAllowed
      ? `Global · ${discount}% discount`
      : 'Global · standard pricing';
  } else if (strategy === 'custom') {
    const count = Object.keys(getAllOverrides(subscriptionType)).length;
    pricingText = `Custom · ${count} ${count === 1 ? 'item' : 'items'} selected`;
  }

  const policyChips = [];
  if (subscriptionType) {
    if (config.coolingOffPeriod.enabled) {
      policyChips.push(`Cooling-off ${config.coolingOffPeriod.days}d`);
    }
    if (subscriptionType !== SubscriptionTypes.ONE_TIME && config.renewal.autoRenew) {
      policyChips.push('Auto-renew');
    }
    if (subscriptionType === SubscriptionTypes.SUBSCRIPTION_NO_CANCELLATION) {
      policyChips.push('No cancellation');
    } else {
      policyChips.push(`Cancel within ${config.cancellation.allowedWithinDays}d`);
    }
    if (showProRata && config.billing.proRataRefund) {
      policyChips.push('Pro-rata refund');
    }
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 2,
        borderColor: '#e2e8f0',
        position: {md: 'sticky'},
        top: {md: 96},
      }}>
      <Typography variant="subtitle1" sx={{fontWeight: 700, color: '#1e293b'}}>
        {isEditMode ? 'Editing Configuration' : 'Summary'}
      </Typography>
      <Box display="flex" alignItems="center" gap={1.5} mt={1} mb={1.5}>
        <LinearProgress
          variant="determinate"
          value={(completed / 5) * 100}
          sx={{
            flex: 1,
            height: 6,
            borderRadius: 3,
            bgcolor: '#e2e8f0',
            '& .MuiLinearProgress-bar': {bgcolor: '#16a34a'},
          }}
        />
        <Typography variant="caption" color="textSecondary">
          {completed}/5
        </Typography>
      </Box>

      <Row
        label="Subscription Type"
        value={subscriptionType && SubscriptionTypeLabels[subscriptionType]}
      />
      <Row
        label="Module"
        value={
          moduleType &&
          (ENTITY_CONFIG.SUBSCRIPTION?.[moduleType]?.label || moduleType)
        }
      />
      <Row
        label="Duration"
        value={
          selectedDuration && `${selectedDuration.value} ${selectedDuration.unit}`
        }
      />
      <Row
        label="Country"
        value={selectedCountry && selectedCountry.countryName}
      />
      <Row label="Pricing" value={pricingText} />

      {policyChips.length > 0 && (
        <>
          <Divider sx={{my: 1.5}} />
          <Typography variant="caption" color="textSecondary" display="block" mb={1}>
            Policies
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.75}>
            {policyChips.map(chip => (
              <Chip
                key={chip}
                label={chip}
                size="small"
                color={chip === 'No cancellation' ? 'error' : 'default'}
              />
            ))}
          </Box>
        </>
      )}

      <Divider sx={{my: 2}} />

      <Button
        fullWidth
        variant="contained"
        size="large"
        onClick={onSubmit}
        disabled={isLoading || disabled}
        endIcon={
          isLoading ? <CircularProgress size={18} color="inherit" /> : null
        }
        sx={{
          backgroundColor: isEditMode ? '#d97706' : '#1976d2',
          color: '#fff',
          '&:hover': {
            backgroundColor: isEditMode ? '#b45309' : '#1565c0',
            color: '#fff',
          },
        }}>
        {isLoading
          ? 'Saving...'
          : isEditMode
          ? 'Update Configuration'
          : 'Create Configuration'}
      </Button>
      <Button
        fullWidth
        startIcon={<ArrowBack />}
        onClick={onBack}
        disabled={isLoading}
        sx={{mt: 1}}>
        Cancel
      </Button>
    </Paper>
  );
};

export default SubscriptionSummaryPanel;
